import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    CheckIcon,
    ExclamationCircleIcon,
    LockClosedIcon,
    BookOpenIcon
} from '@heroicons/react/24/outline';
import { challengeApi, errorMessage } from '../api';
import { computeProgress, computeScore, validateForSubmit } from '../engine';
import SectionBlock, { ScorePanel } from '../SectionBlock';
import { Eyebrow, Panel, ProgressBar, RichText, StatusPill, formatDate, timeAgo, useTick } from '../ui';

const SAVE_DELAY = 1200;
const INTRO = '_intro';

function joinList(items) {
    if (items.length <= 1) return items.join('');
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * Student view of one practical challenge. Also used by the admin builder for
 * "Preview as student" — pass `previewChallenge` and nothing is saved.
 */
export default function ChallengeWorkspace({ previewChallenge, onExitPreview }) {
    const params = useParams();
    const challengeId = previewChallenge ? null : params.challengeId;
    const preview = Boolean(previewChallenge);
    const [searchParams, setSearchParams] = useSearchParams();
    useTick();

    const [loading, setLoading] = useState(!preview);
    const [loadError, setLoadError] = useState(null);
    const [challenge, setChallenge] = useState(previewChallenge || null);
    const [lock, setLock] = useState({ locked: false });
    const [submission, setSubmission] = useState(null);
    const [answers, setAnswers] = useState({});
    const [files, setFiles] = useState([]);
    const [saveState, setSaveState] = useState('idle');
    const [lastSaved, setLastSaved] = useState(null);
    const [attempted, setAttempted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [previewPart, setPreviewPart] = useState(null);

    const answersRef = useRef(answers);
    const dirtyRef = useRef(false);
    const timerRef = useRef(null);
    const savingRef = useRef(null);

    useEffect(() => {
        if (preview) {
            setChallenge(previewChallenge);
            return;
        }
        let cancelled = false;
        setLoading(true);
        challengeApi.get(challengeId)
            .then((data) => {
                if (cancelled) return;
                setChallenge(data.challenge);
                setLock({ locked: data.locked, locked_reason: data.locked_reason });
                setSubmission(data.submission);
                const initial = data.submission ? data.submission.answers : {};
                setAnswers(initial);
                answersRef.current = initial;
                setFiles(data.submission ? data.submission.files : []);
                setLastSaved(data.submission ? data.submission.last_saved_at : null);
            })
            .catch((error) => !cancelled && setLoadError(errorMessage(error, 'Could not load this challenge')))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [challengeId, preview, previewChallenge]);

    const status = submission ? submission.status : 'not_started';
    const readOnly = !preview && (lock.locked || ['submitted', 'approved'].includes(status));
    const layout = challenge && challenge.layout === 'workbook' ? 'workbook' : 'single';

    const flush = useCallback(async () => {
        clearTimeout(timerRef.current);
        if (preview || !dirtyRef.current) return savingRef.current;
        dirtyRef.current = false;
        setSaveState('saving');
        const run = (async () => {
            try {
                const saved = await challengeApi.save(challengeId, answersRef.current);
                setSubmission((prev) => ({ ...(prev || {}), ...saved, answers: prev ? prev.answers : saved.answers }));
                setLastSaved(saved.last_saved_at);
                setSaveState(dirtyRef.current ? 'pending' : 'saved');
            } catch (error) {
                dirtyRef.current = true;
                setSaveState('error');
                toast.error(errorMessage(error, 'Could not save your answers'));
            }
        })();
        savingRef.current = run;
        await run;
        savingRef.current = null;
        return undefined;
    }, [challengeId, preview]);

    const setAnswer = useCallback((key, value) => {
        setAnswers((prev) => {
            const next = { ...prev, [key]: value };
            answersRef.current = next;
            return next;
        });
        if (preview) return;
        dirtyRef.current = true;
        setSaveState('pending');
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flush, SAVE_DELAY);
    }, [flush, preview]);

    useEffect(() => {
        const warn = (e) => {
            if (dirtyRef.current) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', warn);
        return () => {
            window.removeEventListener('beforeunload', warn);
            if (dirtyRef.current) flush();
        };
    }, [flush]);

    const progress = useMemo(() => (challenge ? computeProgress(challenge, answers, files) : null), [challenge, answers, files]);
    const score = useMemo(() => (challenge ? computeScore(challenge, answers) : null), [challenge, answers]);
    const validation = useMemo(() => (challenge ? validateForSubmit(challenge, answers, files) : { ok: false, problems: [] }), [challenge, answers, files]);
    const invalidKeys = useMemo(
        () => new Set(attempted ? validation.problems.flatMap((p) => p.field_keys) : []),
        [attempted, validation]
    );
    const comments = useMemo(() => (submission && submission.comments) || [], [submission]);
    const commentsByField = useMemo(() => {
        const out = {};
        for (const c of comments) if (c.field_key) (out[c.field_key] = out[c.field_key] || []).push(c);
        return out;
    }, [comments]);

    const sections = useMemo(() => (challenge ? challenge.sections.filter((s) => (s.fields || []).length) : []), [challenge]);
    const statusByKey = useMemo(
        () => Object.fromEntries((progress ? progress.sections : []).map((s) => [s.key, s])),
        [progress]
    );

    // Workbook: which part is open. New workbooks start on the instructions.
    const defaultPart = useMemo(() => {
        if (!progress) return INTRO;
        if (!submission) return INTRO;
        const firstOpen = progress.sections.find((s) => !s.done);
        return firstOpen ? firstOpen.key : sections[sections.length - 1]?.key;
    }, [progress, submission, sections]);
    const urlPart = preview ? previewPart : searchParams.get('part');
    const currentKey = urlPart && (urlPart === INTRO || sections.some((s) => s.key === urlPart)) ? urlPart : defaultPart;
    const currentIndex = sections.findIndex((s) => s.key === currentKey);
    const currentSection = currentIndex >= 0 ? sections[currentIndex] : null;

    const goToPart = (key) => {
        flush();
        if (preview) setPreviewPart(key);
        else setSearchParams(key ? { part: key } : {});
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async () => {
        setAttempted(true);
        if (!validation.ok) {
            toast.error('Some required answers are still missing');
            // Workbook: the banner above the button lists the missing parts.
            if (layout === 'single') {
                setTimeout(() => {
                    const key = validation.problems[0]?.field_keys?.[0];
                    document.querySelector(`[data-field="${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 50);
            }
            return;
        }
        if (preview) {
            toast.success('Preview only — nothing was submitted');
            return;
        }
        setSubmitting(true);
        try {
            clearTimeout(timerRef.current);
            await savingRef.current;
            dirtyRef.current = false;
            const saved = await challengeApi.submit(challengeId, answersRef.current);
            setSubmission(saved);
            setLastSaved(saved.last_saved_at);
            setSaveState('saved');
            toast.success(saved.status === 'approved' ? 'Challenge completed' : 'Submitted for mentor review');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            toast.error(errorMessage(error, 'Could not submit'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveDraft = async () => {
        if (preview) return;
        dirtyRef.current = true;
        await flush();
        toast.success('Draft saved');
    };

    const handleRecall = async () => {
        try {
            const saved = await challengeApi.recall(challengeId);
            setSubmission(saved);
            toast.success('Submission recalled — you can edit it again');
        } catch (error) {
            toast.error(errorMessage(error, 'Could not recall the submission'));
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <div className="h-8 w-64 animate-pulse rounded bg-ink-100" />
                <div className="h-40 animate-pulse rounded-2xl bg-ink-100" />
                <div className="h-64 animate-pulse rounded-2xl bg-ink-100" />
            </div>
        );
    }
    if (loadError || !challenge) {
        return (
            <Panel className="text-center">
                <p className="text-sm text-ink-700">{loadError || 'Challenge not found'}</p>
                <Link to="/practical-challenges" className="btn-secondary mt-4 inline-flex">Back to challenges</Link>
            </Panel>
        );
    }
    if (lock.locked) {
        return (
            <Panel className="mx-auto max-w-xl text-center">
                <LockClosedIcon className="mx-auto h-8 w-8 text-ink-400" />
                <h1 className="mt-3 text-lg font-bold text-ink-950">{challenge.title} is locked</h1>
                <p className="mt-1 text-sm text-ink-600">{lock.locked_reason}</p>
                <Link to="/practical-challenges" className="btn-secondary mt-5 inline-flex">Back to challenges</Link>
            </Panel>
        );
    }

    const saveLabel = preview ? 'Preview — answers are not saved'
        : saveState === 'saving' ? 'Saving…'
            : saveState === 'pending' ? 'Unsaved changes'
                : saveState === 'error' ? 'Not saved — retrying on next change'
                    : lastSaved ? (layout === 'workbook' ? 'All changes saved' : `Draft saved · ${timeAgo(lastSaved)}`) : null;

    const sectionComments = comments.filter((c) => !c.field_key);
    const banner = (
        <StatusBanner
            status={status}
            submission={submission}
            settings={challenge.settings}
            commentCount={comments.length}
            onRecall={handleRecall}
        />
    );

    const submitBar = !readOnly && (
        <SubmitBar
            layout={layout}
            validation={validation}
            challenge={challenge}
            attempted={attempted}
            submitting={submitting}
            onSave={handleSaveDraft}
            onSubmit={handleSubmit}
            preview={preview}
            resubmit={status === 'changes_requested'}
            approvalRequired={challenge.settings.mentor_approval_required}
        />
    );

    const sectionProps = {
        layout,
        answers,
        setAnswer,
        files,
        setFiles,
        readOnly,
        uploadsDisabled: preview,
        challengeId,
        invalidKeys,
        commentsByField
    };

    if (layout === 'single') {
        return (
            <div className="mx-auto w-full max-w-5xl space-y-5 pb-12">
                {preview && <PreviewBar onExit={onExitPreview} />}
                <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div className="min-w-0">
                        <Eyebrow>
                            <Link to="/practical-challenges" className="underline decoration-ink-300 underline-offset-4 hover:text-brand-700">Practical challenges</Link>
                            <span className="mx-1.5">/</span>Challenge {challenge.number || 1}
                        </Eyebrow>
                        <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-ink-950 md:text-[1.75rem]">{challenge.title}</h1>
                        {challenge.lead && <p className="mt-1 text-sm text-ink-600">{challenge.lead}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                        {saveLabel && <span className="text-xs text-ink-500">{saveLabel}</span>}
                        <StatusPill status={status === 'not_started' ? 'in_progress' : status} label={status === 'not_started' ? 'Not started' : undefined} />
                    </div>
                </header>

                {banner}
                <SectionComments comments={sectionComments} />

                {(challenge.minimum || []).length > 0 && (
                    <Panel>
                        <h2 className="text-lg font-bold text-ink-950">The minimum</h2>
                        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                            {challenge.minimum.map((m, i) => (
                                <div key={i} className="rounded-xl bg-ink-50 px-4 py-3">
                                    <p className="text-xl font-bold text-ink-950">{m.value}</p>
                                    <p className="mt-0.5 text-xs text-ink-500">{m.label}</p>
                                </div>
                            ))}
                        </div>
                        {challenge.tip && <p className="mt-3 text-xs text-ink-600"><RichText text={challenge.tip} /></p>}
                    </Panel>
                )}

                <Instructions challenge={challenge} collapsible />

                {sections.map((section) => (
                    <SectionBlock key={section.key} section={section} {...sectionProps} />
                ))}

                <TopicDetails challenge={challenge} answers={answers} />
                {submitBar}
            </div>
        );
    }

    // Workbook
    const isLast = currentIndex === sections.length - 1;
    const prev = currentIndex > 0 ? sections[currentIndex - 1] : (currentKey !== INTRO ? { key: INTRO, title: 'Instructions' } : null);
    const next = currentKey === INTRO ? sections[0] : sections[currentIndex + 1];
    const showScore = currentSection && currentSection.show_score_panel && challenge.settings.show_auto_score && score;

    return (
        <div className="pb-12">
            {preview && <PreviewBar onExit={onExitPreview} />}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[230px_minmax(0,1fr)]">
                <PartNav
                    challenge={challenge}
                    sections={sections}
                    statusByKey={statusByKey}
                    progress={progress}
                    currentKey={currentKey}
                    onSelect={goToPart}
                    preview={preview}
                />

                <div className="min-w-0 space-y-5">
                    {currentKey === INTRO ? (
                        <>
                            <header>
                                <Eyebrow>{challenge.title} / Instructions</Eyebrow>
                                <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-ink-950 md:text-[1.75rem]">Before you start</h1>
                                {challenge.lead && <p className="mt-1 text-sm text-ink-600">{challenge.lead}</p>}
                            </header>
                            {banner}
                            <Instructions challenge={challenge} />
                        </>
                    ) : (
                        <>
                            <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                                <div className="min-w-0">
                                    <Eyebrow>{currentSection.part_label || `Part ${currentSection.key} · ${currentSection.title}`}</Eyebrow>
                                    <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-ink-950 md:text-[1.75rem]">{currentSection.heading || currentSection.title}</h1>
                                    {currentSection.description && <p className="mt-1 max-w-3xl text-sm text-ink-600">{currentSection.description}</p>}
                                </div>
                                {saveLabel && (
                                    <span className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full bg-ink-100 px-2.5 py-1 text-xs text-ink-600 sm:self-auto">
                                        {saveState === 'saved' || (saveState === 'idle' && lastSaved) ? <CheckIcon className="h-3.5 w-3.5" /> : null}
                                        {saveLabel}
                                    </span>
                                )}
                            </header>
                            {banner}
                            <SectionComments comments={sectionComments.filter((c) => c.section_key === currentKey)} />

                            {showScore ? (
                                <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_260px]">
                                    <SectionBlock section={currentSection} {...sectionProps} hideFirstHeader />
                                    <div><ScorePanel score={score} className="xl:sticky xl:top-20" /></div>
                                </div>
                            ) : (
                                <SectionBlock section={currentSection} {...sectionProps} hideFirstHeader />
                            )}

                            {isLast && submitBar}
                        </>
                    )}

                    <div className="flex items-center justify-between gap-3 pt-1">
                        {prev ? (
                            <button type="button" onClick={() => goToPart(prev.key)} className="btn-secondary inline-flex items-center gap-1.5">
                                <ArrowLeftIcon className="h-4 w-4" /> {prev.key === INTRO ? 'Instructions' : `Part ${prev.key}`}
                            </button>
                        ) : <span />}
                        {next && (
                            <button type="button" onClick={() => goToPart(next.key)} className="btn-primary inline-flex items-center gap-1.5">
                                Next: Part {next.key} <ArrowRightIcon className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function PreviewBar({ onExit }) {
    return (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
            <span><span className="font-semibold">Preview as student.</span> Answers and uploads are not saved.</span>
            {onExit && <button type="button" onClick={onExit} className="btn-secondary">Back to builder</button>}
        </div>
    );
}

function PartNav({ challenge, sections, statusByKey, progress, currentKey, onSelect, preview }) {
    return (
        <nav aria-label="Workbook parts" className="lg:sticky lg:top-20 lg:self-start">
            {!preview && (
                <Link to="/practical-challenges" className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-brand-700">
                    <ArrowLeftIcon className="h-3.5 w-3.5" /> Back to challenges
                </Link>
            )}
            <p className="mt-2 text-sm font-bold text-ink-950">{challenge.title}</p>
            <div className="mt-1.5 flex items-center justify-between text-xs text-ink-500">
                <span>{progress.done_count} of {progress.total_sections} parts done</span>
                <span className="font-semibold text-ink-900">{progress.percent}%</span>
            </div>
            <ProgressBar percent={progress.percent} className="mt-1.5" />

            <ol className="mt-4 flex gap-1 overflow-x-auto pb-1 lg:block lg:space-y-0.5 lg:overflow-visible">
                <li className="shrink-0">
                    <button
                        type="button" onClick={() => onSelect(INTRO)}
                        aria-current={currentKey === INTRO ? 'step' : undefined}
                        className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] ${currentKey === INTRO ? 'bg-brand-50 font-semibold text-brand-800' : 'text-ink-700 hover:bg-ink-50'}`}
                    >
                        <BookOpenIcon className="h-5 w-5 shrink-0 text-ink-400" />
                        <span className="whitespace-nowrap">Instructions</span>
                    </button>
                </li>
                {sections.map((section) => {
                    const s = statusByKey[section.key];
                    const active = section.key === currentKey;
                    return (
                        <li key={section.key} className="shrink-0">
                            <button
                                type="button" onClick={() => onSelect(section.key)}
                                aria-current={active ? 'step' : undefined}
                                className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] ${active ? 'bg-brand-50 font-semibold text-brand-800' : 'text-ink-700 hover:bg-ink-50'}`}
                            >
                                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${s && s.done
                                    ? 'bg-brand-700 text-white'
                                    : active ? 'border border-brand-600 text-brand-700' : 'border border-ink-300 text-ink-500'}`}
                                >
                                    {s && s.done ? <CheckIcon className="h-3 w-3" /> : section.key}
                                </span>
                                <span className="whitespace-nowrap lg:whitespace-normal">{section.key} · {section.title}</span>
                            </button>
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}

function Instructions({ challenge, collapsible }) {
    const blocks = challenge.instructions || [];
    if (!blocks.length) return null;
    const body = (
        <div className="space-y-4">
            {blocks.map((block, i) => (
                <div key={i}>
                    {block.title && <h3 className="text-sm font-bold text-ink-950">{block.title}</h3>}
                    {(block.paragraphs || []).map((p, j) => <p key={j} className="mt-1.5 text-sm leading-relaxed text-ink-700">{p}</p>)}
                    {(block.bullets || []).length > 0 && (
                        <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm text-ink-700">
                            {block.bullets.map((b, j) => <li key={j}>{b}</li>)}
                        </ul>
                    )}
                </div>
            ))}
        </div>
    );
    if (!collapsible) return <Panel>{body}</Panel>;
    return (
        <details className="group rounded-2xl border border-border bg-white p-5 md:p-6">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                    <BookOpenIcon className="h-4 w-4 text-brand-700" /> How to complete the challenge
                </span>
                <span className="text-xs text-ink-500 group-open:hidden">Read the instructions</span>
                <span className="hidden text-xs text-ink-500 group-open:inline">Hide</span>
            </summary>
            <div className="mt-4 border-t border-border pt-4">{body}</div>
        </details>
    );
}

/** Conversation prompts for the chosen topic, shown under the form. */
function TopicDetails({ challenge, answers }) {
    const field = challenge.sections.flatMap((s) => s.fields || []).find((f) => f.type === 'single_choice' && f.display === 'cards' && (f.options || []).some((o) => o.details));
    if (!field) return null;
    const chosen = (field.options || []).find((o) => o.value === (answers[field.key] || {}).value);
    if (!chosen || !chosen.details) return null;
    return (
        <Panel className="bg-brand-50/40">
            <Eyebrow>Your topic</Eyebrow>
            <h2 className="mt-1 text-base font-bold text-ink-950">{chosen.label}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-ink-700">{chosen.details}</p>
        </Panel>
    );
}

function SectionComments({ comments }) {
    if (!comments || !comments.length) return null;
    return (
        <div className="space-y-2">
            {comments.map((c) => (
                <div key={c.id} className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950">
                    <span className="font-semibold">Mentor comment{c.section_key ? ` on Part ${c.section_key}` : ''}:</span> {c.text}
                </div>
            ))}
        </div>
    );
}

function StatusBanner({ status, submission, settings, commentCount, onRecall }) {
    if (status === 'submitted') {
        return (
            <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-amber-900">
                    <span className="font-semibold">Submitted {formatDate(submission.submitted_at)}</span> — your mentor is reviewing it. Your answers are read-only for now.
                </p>
                {settings.allow_resubmission && (
                    <button type="button" onClick={onRecall} className="btn-secondary shrink-0">Recall to edit</button>
                )}
            </div>
        );
    }
    if (status === 'changes_requested') {
        return (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                <p className="font-semibold">Your mentor returned this for changes</p>
                {submission.mentor_feedback && <p className="mt-1 whitespace-pre-line">{submission.mentor_feedback}</p>}
                <p className="mt-1 text-xs text-red-800">
                    {commentCount ? `${commentCount} comment${commentCount === 1 ? '' : 's'} on your answers — look for the blue notes. ` : ''}
                    Make your changes, then submit again.
                </p>
            </div>
        );
    }
    if (status === 'approved') {
        return (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                <p className="font-semibold">Approved {submission.reviewed_at ? formatDate(submission.reviewed_at) : ''}</p>
                {submission.mentor_feedback && <p className="mt-1 whitespace-pre-line">{submission.mentor_feedback}</p>}
            </div>
        );
    }
    return null;
}

function SubmitBar({ layout, validation, challenge, attempted, submitting, onSave, onSubmit, preview, resubmit, approvalRequired }) {
    const checklistKeys = new Set(challenge.sections.flatMap((s) => (s.fields || []).filter((f) => f.type === 'checklist').map((f) => f.key)));
    const checklistItems = challenge.sections.flatMap((s) => (s.fields || []).filter((f) => f.type === 'checklist')).reduce((n, f) => n + (f.items || []).length, 0);
    const checklistOpen = validation.problems.some((p) => p.field_keys.some((k) => checklistKeys.has(k)));
    const others = validation.problems.filter((p) => !p.field_keys.some((k) => checklistKeys.has(k))).map((p) => p.message);
    const showBanner = !validation.ok && (layout === 'workbook' || attempted);
    const label = approvalRequired ? (resubmit ? 'Resubmit for mentor review' : 'Submit for mentor review') : 'Mark as completed';
    const disabled = submitting || (layout === 'workbook' && !validation.ok);

    return (
        <div className="space-y-4">
            {showBanner && (
                <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <ExclamationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <p>
                        {checklistOpen && `Tick all ${checklistItems} items to submit. `}
                        {others.length > 0 && `${checklistOpen ? 'Also missing' : 'Still missing'}: ${joinList(others)}.`}
                    </p>
                </div>
            )}
            <div className="flex flex-wrap items-center justify-end gap-3">
                {!preview && <button type="button" onClick={onSave} className="btn-secondary">Save draft</button>}
                <button type="button" onClick={onSubmit} disabled={disabled} className="btn-primary disabled:cursor-not-allowed disabled:opacity-50">
                    {submitting ? 'Submitting…' : label}
                </button>
            </div>
        </div>
    );
}
